import React from "react";
import { Link } from "react-router-dom";

import { AuthCard } from "../../../common/u1/AuthCard";
import { AuthHeader } from "../../../common/u1/AuthHeader";
import { PasswordInput } from "../../../common/u1/PasswordInput";
import { PasswordRequirements } from "../../../common/u1/PasswordRequirements";
import { isPasswordValid } from "../../../common/u1/passwordRules";
import { Button } from "../../../common/u1/Button";
import { ErrorNote } from "../../../common/u1/ErrorNote";
import { Modal } from "../../../common/Modal";
import { useSetPassword } from "./useSetPassword";
import { ArrowRight, AlertIcon, CheckIcon } from "../../../../assets/ui/icons";

/* Step 3 — the new password. */
export const PasswordStep = ({ route = null }) => {
  const {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    showPassword,
    toggleShowPassword,
    passwordError,
    isLoading,
    isOpen,
    newUser,
    isVerifyingToken,
    isTokenInvalid,
    tokenErrorMessage,
    handleSubmit,
    handleNavigate,
  } = useSetPassword({ route });

  /* Transcribed render order: the verifying state is checked BEFORE the invalid
     one. See F-091 — with no credentials in the URL the live screen never
     leaves this branch. Preserved. */
  if (isVerifyingToken) {
    return (
      <AuthCard>
        <div
          role="status"
          aria-live="polite"
          className="flex flex-col items-center gap-3 py-8 text-center"
        >
          <span
            aria-hidden="true"
            className="h-8 w-8 rounded-full border-2 border-[var(--line)] border-t-[var(--orange)] animate-spin"
          />
          <p className="m-0 text-[12.5px] text-[var(--muted)] font-sans">
            Verifying your password reset link…
          </p>
        </div>
      </AuthCard>
    );
  }

  if (isTokenInvalid) {
    return (
      <AuthCard>
        <div className="w-full text-center">
          <span
            aria-hidden="true"
            className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--coral)]/[0.08] border border-[var(--coral)]/25 text-[var(--coral-deep)]"
          >
            <AlertIcon />
          </span>

          {/* The heading is the live wording. */}
          <AuthHeader title="Link expired" subtitle={tokenErrorMessage} />

          <div className="mt-6">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] font-sans"
            >
              <ArrowRight size={14} className="rotate-180" /> Back to log in
            </Link>
          </div>
        </div>
      </AuthCard>
    );
  }

  const canSubmit = isPasswordValid(password) && Boolean(confirmPassword) && !isLoading;

  return (
    <>
      {isOpen && (
        /* Transcribed: a brand-new user cannot dismiss this by the backdrop —
           `noClose` is false for them so the close control is offered, and true
           for everyone else. Either way modalHandle runs handleNavigate. */
        <Modal noClose={!newUser} modalHandle={handleNavigate}>
          <div className="flex flex-col items-center gap-5 p-2 text-center">
            <span
              aria-hidden="true"
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--success)]/10 text-[var(--success)]"
            >
              <CheckIcon size={26} />
            </span>

            <div className="flex flex-col gap-2">
              <p className="m-0 text-[18px] font-bold text-[var(--ink)] font-sans">
                {newUser
                  ? "Thank you for registering with TREVEX!"
                  : "Password changed successfully"}
              </p>
              <p className="m-0 max-w-[340px] text-[13px] leading-[1.55] text-[var(--muted)] font-sans">
                {newUser
                  ? "Our representative will reach out shortly to confirm your details and next steps."
                  : "Your password has been successfully changed."}
              </p>
            </div>

            <Button variant="primary" size="lg" className="w-full" onClick={handleNavigate}>
              {newUser ? "Sign in to your account" : "Back to log in"}
              <ArrowRight size={14} />
            </Button>
          </div>
        </Modal>
      )}

      <AuthCard>
        <form onSubmit={handleSubmit} className="w-full">
          <AuthHeader
            title="Create new password"
            subtitle="Please choose a password you have not used on TREVEX before."
          />

          <div className="mt-7 grid gap-3.5">
            <PasswordInput
              id="new-password"
              name="new-password"
              size="lg"
              label="New password"
              autoComplete="new-password"
              placeholder="Your new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              visible={showPassword}
              onToggleVisibility={toggleShowPassword}
            />

            <PasswordInput
              id="confirm-password"
              name="confirm-password"
              size="lg"
              label="Confirm password"
              autoComplete="new-password"
              placeholder="Repeat your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              /* Transcribed: ONE toggle drives both fields on the live screen,
                 so revealing one reveals the other. */
              visible={showPassword}
              onToggleVisibility={toggleShowPassword}
            />
          </div>

          <PasswordRequirements value={password} />

          {passwordError && <ErrorNote>{passwordError}</ErrorNote>}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="mt-5 w-full"
            disabled={!canSubmit}
          >
            {isLoading ? "Saving…" : "Next"}
          </Button>
        </form>
      </AuthCard>
    </>
  );
};

export default PasswordStep;
